import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { Typography, styled } from '@mui/material';
import clsx from 'clsx';
import { ResponsiveContainer } from 'recharts';

import { useChartContainerZoomHandlers } from './_hooks/useChartContainerZoomHandlers';
import { ChartComponentMap, ChartComponentProps } from './_types';
import { determineChartType } from './_utils';
import { ChartActions } from './ChartActions';
import { ChartContainerProps } from './types';
import { WithRef } from '../../../../types';
import { ChartProvider, useChartContext } from '../../ChartProvider';
import { HoverProvider, useIsChartHovered } from '../../HoverProvider';
import { TooltipConfigProvider } from '../../TooltipConfigProvider';
import { ReferenceArea } from '../referenceArea';
import { LoadingOverlay } from './_components/LoadingOverlay';
import { NoDataOverlay } from './_components/NoDataOverlay';

type ChartContainerRootProps<T = any> = Omit<
  ChartContainerProps<T>,
  'data' | 'zoomProps'
>;

const defaultMargin = { top: 24, bottom: 0, left: 0, right: 0 };

const buildChartComponentProps = ({
  margin,
  layout,
  syncId,
  onClick,
  barConfig,
}: Omit<ChartContainerRootProps, 'children'>): ChartComponentProps => {
  return {
    margin: {
      ...defaultMargin,
      ...margin,
    },
    layout,
    syncId,
    onClick: ({ activeIndex, activeCoordinate }, event) =>
      onClick?.(
        {
          activeIndex: activeIndex !== null ? activeIndex : undefined,
          activeCoordinate,
        },
        event
      ),
    ...barConfig,
  };
};

const chartContainerClasses = {
  root: 'ChartContainer-Root',
  container: 'ChartContainer-Container',
  body: 'ChartContainer-Body',
  title: 'ChartContainer-Title',
};

// recharts applies inline styles to ResponsiveContainer (width, height, minWidth, minHeight, maxHeight),
// so its not possible to directly control all of its styles, therefore creating this wrapper
const Root = styled('div', {
  name: 'DsChartContainer',
  slot: 'Root',
  target: chartContainerClasses.root,
  overridesResolver: (_, styles) => [styles.root],
})({});

const Title = styled(Typography, {
  name: 'DsChartContainer',
  slot: 'Title',
  target: chartContainerClasses.title,
  overridesResolver: (_, styles) => [styles.title],
})({});

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  name: 'DsChartContainer',
  slot: 'Container',
  target: chartContainerClasses.container,
  overridesResolver: (_, styles) => [styles.container],
})({});

const ChartContainerRoot = ({
  ref: forwardedRef,
  children,
  sx,
  onResize,
  testId = 'chart-container',
  title,
  titleSx,
  chartActionsSx,
  isLoading,
  hasNoData: _hasNoData,
  noDataOverlayProps,
  ...props
}: WithRef<HTMLDivElement, ChartContainerRootProps>) => {
  const { data, chartType, refAreaLeft, refAreaRight, zoomAxisIds } =
    useChartContext();

  const { onHoverChange } = useIsChartHovered();
  const { onMouseDown, onMouseMove } = useChartContainerZoomHandlers();

  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => containerRef.current!, []);

  useEffect(() => {
    if (containerRef.current && testId) {
      containerRef.current.setAttribute('data-testid', `${testId}-root`);
    }
  }, [testId]);

  const ChartComponent = ChartComponentMap[chartType];

  const chartComponentProps = useMemo(
    () => buildChartComponentProps(props),
    [props]
  );

  const hasNoData = _hasNoData ?? !data.length;

  const renderBody = () => {
    if (isLoading) {
      return (
        <LoadingOverlay
          margin={chartComponentProps.margin}
          testId={`${testId}-loading`}
        />
      );
    }

    if (hasNoData) {
      return (
        <NoDataOverlay
          userProps={noDataOverlayProps}
          testId={`${testId}-no-data`}
        />
      );
    }

    return (
      <StyledResponsiveContainer
        onResize={onResize}
        className={chartContainerClasses.container}
        ref={containerRef}
      >
        <ChartComponent
          {...chartComponentProps}
          onMouseEnter={() => onHoverChange(true)}
          onMouseLeave={() => onHoverChange(false)}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          className={clsx(chartContainerClasses.body, chartType)}
          data={data}
          data-testid={`${testId}-body`}
        >
          {children}
          {data.length > 0 && refAreaLeft && refAreaRight ? (
            <ReferenceArea
              data-testid="chart-zoom-area"
              x1={refAreaLeft}
              x2={refAreaRight}
              xAxisId={zoomAxisIds.x}
              yAxisId={zoomAxisIds.y}
              fillOpacity={0.15}
            />
          ) : null}
        </ChartComponent>
      </StyledResponsiveContainer>
    );
  };

  return (
    <Root sx={sx} data-testid={testId}>
      {!!title && typeof title === 'string' ? (
        <Title data-testid={`${testId}-title`} sx={titleSx}>
          {title}
        </Title>
      ) : (
        title
      )}
      {!isLoading && (
        <ChartActions
          hasNoData={hasNoData}
          sx={chartActionsSx}
          analyzeWithOpsPilot={props.analyzeWithOpsPilot}
        />
      )}
      {renderBody()}
    </Root>
  );
};

export const ChartContainer = <T,>({
  ref,
  data,
  zoomProps,
  clusteringProps,
  highlightingProps,
  fallbackChartType = 'composed',
  ...props
}: WithRef<HTMLDivElement, ChartContainerProps<T>>) => {
  const { chartBlocks, chartType } = determineChartType(
    props.children,
    fallbackChartType,
    !!zoomProps
  );

  return (
    <ChartProvider<T>
      chartBlocks={chartBlocks}
      chartType={chartType}
      data={data}
      syncId={props.syncId}
      zoomProps={zoomProps}
      clusteringProps={clusteringProps}
      highlightingProps={highlightingProps}
      barSize={props.barConfig?.barSize}
    >
      <HoverProvider>
        <TooltipConfigProvider<T>>
          <ChartContainerRoot {...props} ref={ref} />
        </TooltipConfigProvider>
      </HoverProvider>
    </ChartProvider>
  );
};
